import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { Search, Building2, Award, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { getBuildingNameClass, getSsDueTextClassFromDays } from '../utils/ssDueColor';
import { daysUntilDue, formatDueDateLocal } from '../utils/dueDate';
import { JOB_STATUS_LABELS, mailListLabel } from '../utils/jobStatusColor';
import { InfoButton } from './InfoModal';
import ForceMoveControl from './ForceMoveControl';
import { buildingInspectionsPath, issueManuallyPath, ISSUE_LABELS } from '../constants/jobModel';

const BUCKETS = [
  { id: 'all', label: 'All (??0d)' },
  { id: 'overdue', label: 'Overdue (past)' },
  { id: 'urgent', label: 'Urgent (0??0d left)' },
  { id: 'upcoming', label: 'Upcoming (31??0d left)' },
];

const BUCKET_LABEL = {
  overdue: 'Overdue',
  urgent: 'Urgent',
  upcoming: 'Upcoming',
};

/** Bulk-only actions (multi-select). Per-SS progress move is on each row. */
const BULK_ACTIONS = [
  { value: 'email', label: 'Email overdue/urgent digest' },
  { value: 'schedule', label: 'Add selected to Schedule' },
];

const FOCUS_TITLE = {
  overdue: 'Overdue',
  urgent: 'Urgent',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
};

/**
 * Due queue: per-SS force-move on each row; bulk email/schedule via top dropdown.
 * Dashboard cards pass `focus` so the list filters in place (no page change).
 */
export default function DueQueuePanel({
  rows,
  focus = '',
  onFocusChange,
  scheduledJobs = [],
  inProgressJobs = [],
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bucket, setBucket] = useState('all');
  const statusMode = focus === 'scheduled' || focus === 'in_progress';
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [scheduleDate, setScheduleDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [classifyBusy, setClassifyBusy] = useState(null);

  const invalidateDue = () => {
    queryClient.invalidateQueries({ queryKey: ['buildings'] });
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.invalidateQueries(['jobs', 'overdue']);
    queryClient.invalidateQueries(['jobs', 'urgent']);
    queryClient.invalidateQueries(['jobs', 'upcoming-due']);
    queryClient.invalidateQueries(['jobs', 'dashboard-summary']);
    queryClient.invalidateQueries(['buildings', 'overdue']);
    queryClient.invalidateQueries(['buildings', 'due-soon']);
    queryClient.invalidateQueries(['job-schedule-events']);
    queryClient.invalidateQueries(['jobs', 'schedule-events']);
    queryClient.invalidateQueries(['schedules']);
  };

  const classifySs = async (row, status) => {
    if (!row?.building_id || !row?.ss_code) {
      toast.error('Missing building or SS');
      return;
    }
    const label = status === 'DEFERRED' ? 'Starts later' : 'Ended';
    const resumeHint =
      status === 'DEFERRED'
        ? `\n\nResume date defaults to 1 January next year. Restore anytime from Buildings → Starts later.`
        : `\n\nHidden from Due / Urgent. Restore anytime from Buildings → Ended.`;
    if (
      !window.confirm(
        `${label}: ${row.building_name || 'Building'} · ${row.ss_code}?\n\nThis does not delete data — only removes it from work queues.${resumeHint}`
      )
    ) {
      return;
    }
    const key = `${row.building_id}:${row.ss_code}:${status}`;
    setClassifyBusy(key);
    try {
      await api.put(`/buildings/${row.building_id}/ss-contract`, {
        ssCode: row.ss_code,
        status,
      });
      toast.success(`${row.ss_code} → ${label}`);
      invalidateDue();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Classification failed');
    } finally {
      setClassifyBusy(null);
    }
  };

  useEffect(() => {
    if (focus === 'overdue' || focus === 'urgent') setBucket(focus);
    else if (!focus && (bucket === 'overdue' || bucket === 'urgent')) setBucket('all');
  }, [focus]);

  const filtered = useMemo(() => {
    let list = Array.isArray(rows) ? rows : [];
    const dueFocus = focus === 'overdue' || focus === 'urgent' ? focus : bucket;
    if (dueFocus !== 'all') list = list.filter((r) => r.bucket === dueFocus);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const bits = [r.building_name, r.ss_code, r.ss_name, r.job_id, r.assigned_worker_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return bits.includes(q);
      });
    }
    return list;
  }, [rows, bucket, search, focus]);

  const statusJobs = useMemo(() => {
    const src = focus === 'scheduled' ? scheduledJobs : focus === 'in_progress' ? inProgressJobs : [];
    const q = search.trim().toLowerCase();
    let list = Array.isArray(src) ? src : [];
    if (q) {
      list = list.filter((j) => {
        const bits = [j.building_name, j.ss_code, j.ss_name, j.id, j.assigned_worker_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return bits.includes(q);
      });
    }
    return list;
  }, [focus, scheduledJobs, inProgressJobs, search]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const keys = filtered.map((r) => r.key);
    setSelected((prev) => {
      const allOn = keys.length > 0 && keys.every((k) => prev.has(k));
      if (allOn) {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      }
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const selectedRows = useMemo(
    () => (Array.isArray(rows) ? rows : []).filter((r) => selected.has(r.key)),
    [rows, selected]
  );

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      const jobIds = selectedRows.map((r) => r.job_id).filter(Boolean);
      const noJob = selectedRows.filter((r) => !r.job_id);
      if (!jobIds.length) {
        throw new Error(
          noJob.length
            ? 'Selected rows have no inspection pack yet ??Issue manually only if auto did not create one.'
            : 'Select at least one inspection row'
        );
      }
      const res = await api.post('/jobs/bulk-schedule', {
        jobIds,
        scheduled_date: scheduleDate,
      });
      return { ...res.data, noJobCount: noJob.length };
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Added to schedule');
      if (data.noJobCount > 0) {
        toast(`${data.noJobCount} row(s) without a pack ??Issue manually only if auto missed them.`, { icon: '?�️' });
      }
      setSelected(new Set());
      setBulkAction('');
      invalidateDue();
      // Stay on Due queue ??Schedule action does not remove from overdue/urgent list
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.message || 'Failed to schedule');
    },
  });

  const emailMutation = useMutation({
    mutationFn: async () => {
      const items = selectedRows.length
        ? selectedRows
        : filtered.filter((r) => r.bucket === 'overdue' || r.bucket === 'urgent');
      if (!items.length) {
        throw new Error('Nothing to email ??select rows or filter Urgent/Overdue');
      }
      const res = await api.post('/jobs/notify-urgent', {
        items: items.map((r) => ({
          building_name: r.building_name,
          ss_code: r.ss_code,
          due_date: r.due_date,
          job_id: r.job_id,
          bucket: r.bucket,
          days: r.days,
        })),
      });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Urgent digest processed');
      setBulkAction('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.message || 'Failed to send');
    },
  });

  const busy = scheduleMutation.isPending || emailMutation.isPending;

  const runBulk = () => {
    if (!bulkAction) {
      toast.error('Choose a bulk action first');
      return;
    }
    if (bulkAction === 'email') {
      if (!window.confirm('Send overdue/urgent digest email now?')) return;
      emailMutation.mutate();
      return;
    }
    if (bulkAction === 'schedule') {
      if (!selectedRows.length) {
        toast.error('Select rows to add to Schedule');
        return;
      }
      if (!scheduleDate) {
        toast.error('Set a schedule date');
        return;
      }
      if (!window.confirm(`Add selected to Schedule on ${scheduleDate}?`)) return;
      scheduleMutation.mutate();
    }
  };

  const bucketBadge = (b) => {
    if (b === 'overdue') return 'bg-red-800 text-white';
    if (b === 'urgent') return 'bg-red-100 text-red-800';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="bg-card rounded-lg shadow mb-8 overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="text-xl font-semibold text-gray-900">{FOCUS_TITLE[focus] || 'Due queue'}</h2>
          <InfoButton title="Due queue">
            <div className="space-y-2 text-sm">
              <p>
                <strong>This is the start of the inspection workflow</strong> for BWOF agent ops. Overdue / Urgent
                SS appear here before (or without) a work pack. Due asks the default IQP for a date; the pack is
                issued automatically. {ISSUE_LABELS.verb} only if that pack is missing.
              </p>
              <p>
                Same source as Dashboard Over Due / Urgent cards. Open the same list on{' '}
                <strong>Inspections ??Due work (start here)</strong>.
              </p>
              <p>
                Completed drops off this queue. Clock (Over Due / Urgent) stays until the due is cleared.
              </p>
            </div>
          </InfoButton>
        </div>
      </div>

      <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
        {!statusMode ? (
          <div className="flex flex-wrap gap-1 p-1 bg-gray-100 rounded-lg">
            {BUCKETS.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => {
                  setBucket(b.id);
                  if (typeof onFocusChange === 'function') {
                    if (b.id === 'overdue' || b.id === 'urgent') onFocusChange(b.id);
                    else onFocusChange('');
                  }
                }}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  (focus === 'overdue' || focus === 'urgent' ? focus : bucket) === b.id
                    ? 'bg-white shadow text-primary-700 font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Tap the card again to show the full Due queue.</p>
        )}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search building, SS, worker…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        {!statusMode ? (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span>Schedule date</span>
            <input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
            />
          </label>
        ) : null}
      </div>

      {!statusMode ? (
      <div className="px-6 py-3 flex flex-wrap items-center gap-3 text-sm border-b border-gray-100">
        <button type="button" onClick={toggleAllVisible} className="text-primary-700 font-medium hover:underline">
          {filtered.length && filtered.every((r) => selected.has(r.key))
            ? 'Clear visible'
            : `Select visible (${filtered.length})`}
        </button>
        <span className="text-gray-500">{selected.size} selected</span>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              disabled={busy}
              className="appearance-none text-sm border border-gray-300 rounded-lg pl-3 pr-8 py-2 bg-white min-w-[14rem]"
              aria-label="Bulk action"
            >
              <option value="">Bulk action…</option>
              {BULK_ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
          <button
            type="button"
            disabled={!bulkAction || busy}
            onClick={runBulk}
            className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
      ) : (
        <div className="px-6 py-2 text-xs text-gray-500 border-b border-gray-100">
          {statusJobs.length} inspection{statusJobs.length === 1 ? '' : 's'}
        </div>
      )}

      <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
        {statusMode ? (
          statusJobs.length === 0 ? (
            <p className="p-8 text-center text-gray-500 text-sm">No items in this filter.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Building / SS</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">IQP</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Classify</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {statusJobs.map((job) => {
                  const days = daysUntilDue(job.due_date);
                  const st = String(job.status || '').toUpperCase();
                  const row = {
                    building_id: job.building_id,
                    building_name: job.building_name,
                    ss_code: job.ss_code,
                  };
                  return (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          className="text-left"
                          onClick={() => {
                            if (job.id) navigate(`/jobs/${job.id}`);
                            else if (job.building_id) navigate(buildingInspectionsPath(job.building_id));
                          }}
                        >
                          <span className="inline-flex items-center gap-1 font-medium text-gray-900">
                            <Building2 className="w-3.5 h-3.5 text-gray-400" />
                            <span className={getBuildingNameClass(days, job.application_approved)}>
                              {job.building_name || 'Building'}
                            </span>
                          </span>
                          <span className="block text-xs mt-0.5">
                            <Award className="w-3 h-3 inline mr-1 text-gray-400" />
                            <span className={getSsDueTextClassFromDays(days)}>{job.ss_code}</span>
                            {job.ss_name && job.ss_name !== job.ss_code ? (
                              <span className="text-gray-500"> — {job.ss_name}</span>
                            ) : null}
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                        {formatDueDateLocal(job.due_date)}
                        {days != null ? (
                          <span className="block text-xs text-gray-500">
                            {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{mailListLabel(job) || JOB_STATUS_LABELS[st] || job.status || '—'}</td>
                      <td className="px-4 py-2 text-gray-600 text-xs">{job.assigned_worker_name || '—'}</td>
                      <td className="px-4 py-2">
                        {job.building_id && job.ss_code ? (
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              disabled={!!classifyBusy}
                              onClick={() => classifySs(row, 'DEFERRED')}
                              className="px-2 py-0.5 rounded border border-sky-300 bg-sky-50 text-[11px] font-semibold text-sky-900"
                            >
                              Starts later
                            </button>
                            <button
                              type="button"
                              disabled={!!classifyBusy}
                              onClick={() => classifySs(row, 'ENDED')}
                              className="px-2 py-0.5 rounded border border-slate-300 bg-slate-50 text-[11px] font-semibold text-slate-800"
                            >
                              Ended
                            </button>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        ) : filtered.length === 0 ? (
          <p className="p-8 text-center text-gray-500 text-sm">No items in this filter.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-2 w-10" />
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bucket</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Building / SS</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Inspection</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[16rem]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((row) => (
                <tr
                  key={row.key}
                  className={`hover:bg-gray-50 ${selected.has(row.key) ? 'bg-primary-50/50' : ''}`}
                >
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(row.key)}
                      onChange={() => toggle(row.key)}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${bucketBadge(row.bucket)}`}>
                      {BUCKET_LABEL[row.bucket] || row.bucket}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      className="text-left"
                      onClick={() => {
                        if (row.job_id) navigate(`/jobs/${row.job_id}`);
                        else if (row.building_id) navigate(buildingInspectionsPath(row.building_id));
                      }}
                    >
                      <span className="inline-flex items-center gap-1 font-medium text-gray-900">
                        <Building2 className="w-3.5 h-3.5 text-gray-400" />
                        <span className={getBuildingNameClass(row.days, row.application_approved)}>
                          {row.building_name}
                        </span>
                      </span>
                      <span className="block text-xs mt-0.5">
                        <Award className="w-3 h-3 inline mr-1 text-gray-400" />
                        <span className={getSsDueTextClassFromDays(row.days)}>{row.ss_code}</span>
                        {row.ss_name && row.ss_name !== row.ss_code ? (
                          <span className="text-gray-500"> — {row.ss_name}</span>
                        ) : null}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                    {formatDueDateLocal(row.due_date)}
                    {row.days != null && (
                      <span className="block text-xs text-gray-500">
                        {row.days < 0 ? `${Math.abs(row.days)}d overdue` : `${row.days}d left`}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {row.job_id ? (
                      `#${row.job_id}`
                    ) : (
                      <button
                        type="button"
                        className="text-xs text-primary-700 hover:underline"
                        onClick={() => navigate(issueManuallyPath(row.building_id))}
                      >
                        {ISSUE_LABELS.verb}
                      </button>
                    )}
                    {row.assigned_worker_name ? (
                      <span className="block text-xs">{row.assigned_worker_name}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <div className="flex flex-col gap-1.5 min-w-[14rem]">
                      <ForceMoveControl
                        compact
                        jobId={row.job_id}
                        buildingId={row.building_id}
                        dueDate={row.due_date}
                        scheduledDate={row.scheduled_date}
                        buildingName={row.building_name}
                        ssCode={row.ss_code}
                        ssName={row.ss_name}
                        bucket={row.bucket}
                        days={row.days}
                        scheduleDate={scheduleDate}
                        currentStatus={row.status || row.job_status}
                      />
                      {row.building_id && row.ss_code && row.ss_code !== '—' ? (
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            disabled={!!classifyBusy}
                            title="Hide from Due queues until resume date (default: 1 Jan next year)"
                            onClick={() => classifySs(row, 'DEFERRED')}
                            className="px-2 py-0.5 rounded border border-sky-300 bg-sky-50 text-[11px] font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-50"
                          >
                            {classifyBusy === `${row.building_id}:${row.ss_code}:DEFERRED`
                              ? '…'
                              : 'Starts later'}
                          </button>
                          <button
                            type="button"
                            disabled={!!classifyBusy}
                            title="Contract ended — hide from Due queues (restore later from Buildings)"
                            onClick={() => classifySs(row, 'ENDED')}
                            className="px-2 py-0.5 rounded border border-slate-300 bg-slate-50 text-[11px] font-semibold text-slate-800 hover:bg-slate-100 disabled:opacity-50"
                          >
                            {classifyBusy === `${row.building_id}:${row.ss_code}:ENDED` ? '…' : 'Ended'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
